import { Service } from './Service';
import http from './ApiConfig';
import { AdicionarSugestaoCampanhaInputType } from '@/types/sugestoesCampanhas/AdicionarSugestaoCampanhaInputType';
import { EditarSugestaoCampanhaInputType } from '@/types/sugestoesCampanhas/EditarSugestaoCampanhaInputType';
import { ObterSugestaoCampanhaOutputType } from '@/types/sugestoesCampanhas/ObterSugestaoCampanhaOutputType';
import { RespostaSugestaoCampanhaOutputType } from '@/types/sugestoesCampanhas/RespostaSugestaoCampanhaOutputType';

export class SugestaoCampanhaService extends Service {
  constructor() {
    super('SugestoesCampanhas');
  }

  async obterSugestoesCampanhas(): Promise<ObterSugestaoCampanhaOutputType[]> {
    try {
      const url = `${this._nomeControle}`;
      const configuracoes = this.GetHeader();
      const response = await http.get<ObterSugestaoCampanhaOutputType[]>(url, configuracoes);
      return response.data;
    } catch (error) {
      console.error('Erro ao obter sugestões de campanhas:', error);
      throw error;
    }
  }

  async obterSugestaoCampanhaPorId(id: number): Promise<ObterSugestaoCampanhaOutputType> {
    try {
      if (id <= 0) {
        throw new Error('ID deve ser um valor positivo válido.');
      }

      const url = `${this._nomeControle}/${id}`;
      const configuracoes = this.GetHeader();
      const response = await http.get<ObterSugestaoCampanhaOutputType>(url, configuracoes);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter sugestão de campanha por ID ${id}:`, error);
      throw error;
    }
  }

  async adicionarSugestaoCampanha(novaSugestaoCampanha: AdicionarSugestaoCampanhaInputType): Promise<RespostaSugestaoCampanhaOutputType> {
    try {
      if (!novaSugestaoCampanha) {
        throw new Error('Dados da sugestão de campanha são obrigatórios.');
      }

      if (!novaSugestaoCampanha.nome || novaSugestaoCampanha.nome.trim() === '') {
        throw new Error('Nome da campanha é obrigatório.');
      }

      if (novaSugestaoCampanha.dataInicio >= novaSugestaoCampanha.dataFim) {
        throw new Error('Data de início deve ser anterior à data de fim.');
      }

      const url = `${this._nomeControle}`;
      const configuracoes = this.GetHeader();
      const response = await http.post<RespostaSugestaoCampanhaOutputType>(url, novaSugestaoCampanha, configuracoes);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar sugestão de campanha:', error);
      throw error;
    }
  }

  async editarSugestaoCampanha(id: number, edicaoSugestaoCampanha: EditarSugestaoCampanhaInputType): Promise<RespostaSugestaoCampanhaOutputType> {
    try {
      if (id <= 0) {
        throw new Error('ID deve ser um valor positivo válido.');
      }

      if (!edicaoSugestaoCampanha) {
        throw new Error('Dados da sugestão de campanha são obrigatórios.');
      }

      if (id !== edicaoSugestaoCampanha.id) {
        throw new Error('ID da URL deve corresponder ao ID no corpo da requisição.');
      }

      if (!edicaoSugestaoCampanha.nome || edicaoSugestaoCampanha.nome.trim() === '') {
        throw new Error('Nome da campanha é obrigatório.');
      }

      if (edicaoSugestaoCampanha.dataInicio >= edicaoSugestaoCampanha.dataFim) {
        throw new Error('Data de início deve ser anterior à data de fim.');
      }

      const url = `${this._nomeControle}/${id}`;
      const configuracoes = this.GetHeader();
      const response = await http.put<RespostaSugestaoCampanhaOutputType>(url, edicaoSugestaoCampanha, configuracoes);
      return response.data;
    } catch (error) {
      console.error(`Erro ao editar sugestão de campanha ${id}:`, error);
      throw error;
    }
  }

  async alternarStatusSugestaoCampanha(id: number): Promise<RespostaSugestaoCampanhaOutputType> {
    try {
      if (id <= 0) {
        throw new Error('ID deve ser um valor positivo válido.');
      }

      const url = `${this._nomeControle}/${id}/alternar-status`;
      const configuracoes = this.GetHeader();
      const response = await http.patch<RespostaSugestaoCampanhaOutputType>(url, {}, configuracoes);
      return response.data;
    } catch (error) {
      console.error(`Erro ao desativar sugestão de campanha ${id}:`, error);
      throw error;
    }
  }

  async finalizarSugestaoCampanha(id: number): Promise<RespostaSugestaoCampanhaOutputType> {
    try {
      if (id <= 0) {
        throw new Error('ID deve ser um valor positivo válido.');
      }

      const url = `${this._nomeControle}/${id}/finalizar`;
      const configuracoes = this.GetHeader();
      const response = await http.patch<RespostaSugestaoCampanhaOutputType>(url, {}, configuracoes);
      return response.data;
    } catch (error) {
      console.error(`Erro ao finalizar sugestão de campanha ${id}:`, error);
      throw error;
    }
  }

  async processarCampanhasExpiradas(): Promise<RespostaSugestaoCampanhaOutputType> {
    try {
      const url = `${this._nomeControle}/processar-expiradas`;
      const configuracoes = this.GetHeader();
      const response = await http.post<RespostaSugestaoCampanhaOutputType>(url, {}, configuracoes);
      return response.data;
    } catch (error) {
      console.error('Erro ao processar campanhas expiradas:', error);
      throw error;
    }
  }

  async marcarCampanhaComoExpirada(id: number): Promise<RespostaSugestaoCampanhaOutputType> {
    return this.finalizarSugestaoCampanha(id);
  }

  private validarDatas(dataInicio: Date, dataFim: Date): boolean {
    return dataInicio < dataFim;
  }

  private validarNome(nome: string): boolean {
    return nome && nome.trim().length > 0;
  }
}
